import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import {
  useTranslate,
  TextInput,
  SelectInput,
  required,
  usePermissions,
  useGetMany,
} from 'react-admin';
import Grid from '@material-ui/core/Grid';
import { Alert } from '@material-ui/lab';
import { useForm, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';
import lodashGet from 'lodash/get';

import { StatusField } from '~/components';
import { HiddenInput, AmountInput, ModalOrSelectInput, ReferenceInput } from '~/components/ra';
import { Business, Terminal } from '~/types';
import { SelectTransactionType } from '~/components/TransactionsHistory/components/SelectTransactionType';
import { getStaticQrOperationTypes } from '~/utils/staticQr';
import useSolution from '~/hooks/useSolution';

interface Props {
  businessRecord: Business;
  record?: any;
}

const PaymentDetailForm = (props: Props) => {
  const { businessRecord } = props;
  const { solutionId, id: businessId } = businessRecord;
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.paymentDetails.${key}`);

  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const terminalId: string | undefined = values.terminalId;

  const terminal: Terminal | undefined = useSelector((state: any) =>
    lodashGet(state, `admin.resources.terminals.data[${terminalId}]`)
  );
  const accountIds: string[] = terminal?.accountIds ?? [];

  const { data: solution } = useSolution(solutionId);
  const licensedOperationTypes = getStaticQrOperationTypes(solution?.features);

  const allOperationTypeChoices = SelectTransactionType.getChoices(translate, true) as any[];
  const licensedOperationTypeChoices = allOperationTypeChoices.filter((choice) =>
    licensedOperationTypes.includes(choice.id)
  );

  // Keep an already saved, but no longer licensed value readable in the select.
  const operationType: string | undefined = values.operationType;
  const isOperationTypeNotLicensed =
    !!solution && !!operationType && !licensedOperationTypes.includes(operationType);
  const notLicensedChoice = allOperationTypeChoices.find(
    (choice) => choice.id === operationType
  ) ?? { id: operationType, name: operationType };

  const operationTypeChoices = isOperationTypeNotLicensed
    ? [...licensedOperationTypeChoices, { ...notLicensedChoice, disabled: true }]
    : licensedOperationTypeChoices;

  const validateOperationType = (value?: string) =>
    !solution || !value || licensedOperationTypes.includes(value)
      ? undefined
      : t('operationTypeNotLicensed');

  const { permissions, loaded: permissionsLoaded } = usePermissions();
  const hasAccountListPermission: boolean = !!permissions?.includes('business.account.list');

  const { data: accounts, loading: accountsLoading } = useGetMany('accounts', accountIds);

  const availableCurrencies = useMemo(() => {
    if (!accounts || accountsLoading) return undefined;
    const seen = new Set<string>();
    const result: { id: string; name: string }[] = [];
    for (const acc of accounts) {
      if (acc?.currency && !seen.has(acc.currency)) {
        seen.add(acc.currency);
        result.push({ id: acc.currency, name: acc.currency });
      }
    }
    return result;
  }, [accounts, accountsLoading]);

  const prevTerminalIdRef = useRef(terminalId);
  useEffect(() => {
    if (prevTerminalIdRef.current !== terminalId) {
      prevTerminalIdRef.current = terminalId;
      form.change('amount.currency', undefined);
    }
  }, [terminalId, form]);

  const currencyDisabled = !terminalId || accountIds.length === 0 || accountsLoading;

  return (
    <Fragment>
      <HiddenInput source='solutionId' defaultValue={solutionId} disabled />
      <HiddenInput source='businessId' defaultValue={businessId} disabled />
      <TextInput source='name' label={t('name')} validate={required()} fullWidth />
      <SelectInput
        source='operationType'
        label={t('operationType')}
        choices={operationTypeChoices}
        disabled={!solution}
        validate={[required(), validateOperationType]}
        fullWidth
      />
      <ReferenceInput
        source='terminalId'
        reference='terminals'
        filter={{ businessId: businessId }}
        label={t('terminal')}
        fullWidth
      >
        <ModalOrSelectInput<Terminal> optionText='label' validate={required()} fullWidth>
          {(item) => {
            return (
              <Grid container spacing={1} alignItems='center' wrap='nowrap'>
                <Grid item>
                  <StatusField status={item.status} variant='dot' />
                </Grid>
                <Grid item>{item.label}</Grid>
              </Grid>
            );
          }}
        </ModalOrSelectInput>
      </ReferenceInput>
      {terminalId && permissionsLoaded && !hasAccountListPermission && (
        <Alert severity='warning'>{t('noAccountListPermissionWarning')}</Alert>
      )}
      <TextInput source='comment' label={t('comment')} fullWidth />
      <AmountInput
        positiveOnly
        nonNullable
        allowEmpty
        currencies={availableCurrencies}
        currencyDisabled={currencyDisabled}
      />
    </Fragment>
  );
};

export default PaymentDetailForm;
